import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "components/ui/button";
import { Link } from "components/ui/link";
import { UITourBanner } from "components/ui/UITourBanner";
import { format } from "date-fns";
import { CheckCircle2, Info, Loader2, Minus, XCircle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router";
import { JobStatusTag } from "src/components/JobStatusTag";
import { ScopeGate } from "src/components/ScopeGate";
import { PageSuspense } from "src/components/ui/page-suspense";
import { SimpleDataTable } from "src/components/ui/SimpleDataTable";
import { FilterableHeader } from "src/components/ui/SimpleDataTable/FilterableHeader";
import { Tooltip } from "src/components/ui/tooltip";
import { useMe, usePermissions } from "src/context/MeContext";

import { DigestJobsQuery } from "./__generated__/DigestJobsQuery.graphql";
import { CreateDigestWizard } from "./CreateDigestWizard";
import { type DigestTemplateKey, getDigestTemplate } from "./CreateDigestWizard/templates";
import DigestEmptyState from "./DigestEmptyState";
import { formatCronExpression, isJobRunTimedOut } from "./utils";

const digestJobsQuery = graphql`
  query DigestJobsQuery {
    me {
      ...appMe
      id
      organization {
        id
        ...CreateDigestWizard_organization
        digestJobs {
          id
          name
          schedule
          status
          team {
            id
            name
          }
          jobRuns(limit: 5, sortBy: "created_at", sortOrder: "desc") {
            edges {
              node {
                id
                status
                createdAt
                updatedAt
              }
            }
          }
        }
      }
    }
  }
`;

interface RecentRunsCellProps {
  runs: ReadonlyArray<{ readonly node: { readonly id: string; readonly status: string; readonly createdAt: string; readonly updatedAt: string } }>;
  formatRunTimestamp: (timestamp: string) => string;
}

const RecentRunsCell = React.memo(function RecentRunsCell({
  runs,
  formatRunTimestamp,
}: RecentRunsCellProps) {
  const navigate = useNavigate();

  // Pad to 5 slots with most recent on the right
  // runs comes newest first, so we reverse to get oldest first, then pad from the left
  const reversedRuns = [...runs].reverse();
  const paddedRuns = Array.from({ length: 5 }, (_, i) => {
    const runIndex = i - (5 - reversedRuns.length);
    return runIndex >= 0 ? reversedRuns[runIndex] : null;
  });

  const handleRunClick = (runId: string, isClickable: boolean) => {
    if (isClickable) {
      navigate(`/digests/run/${runId}`);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      {paddedRuns.map((run, index) => {
        if (!run) {
          return (
            <Tooltip key={`empty-${index}`} content="No run" position="top">
              <Minus className="h-4 w-4 text-muted-foreground" />
            </Tooltip>
          );
        }

        const { status, createdAt, updatedAt } = run.node;
        const timeString = formatRunTimestamp(createdAt);
        const timedOut = isJobRunTimedOut(status, updatedAt);
        const isClickable = status === "COMPLETED" || status === "ERRORED" || status === "STOPPED" || timedOut;

        // Timed out jobs (IN_PROGRESS with no updates for 45+ minutes) show as errored
        if (timedOut) {
          return (
            <Tooltip key={run.node.id} content={timeString} position="top">
              <button
                onClick={() => handleRunClick(run.node.id, isClickable)}
                className="hover:opacity-80 transition-opacity cursor-pointer"
              >
                <XCircle className="h-4 w-4 text-red-500" />
              </button>
            </Tooltip>
          );
        }

        switch (status) {
          case "COMPLETED":
            return (
              <Tooltip key={run.node.id} content={timeString} position="top">
                <button
                  onClick={() => handleRunClick(run.node.id, isClickable)}
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </button>
              </Tooltip>
            );
          case "ERRORED":
          case "STOPPED":
            return (
              <Tooltip key={run.node.id} content={timeString} position="top">
                <button
                  onClick={() => handleRunClick(run.node.id, isClickable)}
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <XCircle className="h-4 w-4 text-red-500" />
                </button>
              </Tooltip>
            );
          case "IN_PROGRESS":
          case "PENDING":
            return (
              <Tooltip key={run.node.id} content={timeString} position="top">
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
              </Tooltip>
            );
          default:
            return (
              <Tooltip key={run.node.id} content={timeString} position="top">
                <Minus className="h-4 w-4 text-muted-foreground" />
              </Tooltip>
            );
        }
      })}
    </div>
  );
});

export function DigestJobsContent() {
  const me = useMe();
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const canModify = canWrite("digests");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState<number | null>(null);
  const [templateKey, setTemplateKey] = useState<DigestTemplateKey | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());

  // Initialize team filter with user's current team context
  const userTeamId = me.teamContext?.team?.id;
  const [teamFilter, setTeamFilter] = useState<Set<string>>(() =>
    userTeamId ? new Set([userTeamId]) : new Set()
  );

  // Sync team filter when global team context changes
  useEffect(() => {
    setTeamFilter(userTeamId ? new Set([userTeamId]) : new Set());
  }, [userTeamId]);

  const data = useLazyLoadQuery<DigestJobsQuery>(
    digestJobsQuery,
    {},
    { fetchPolicy: "network-only", fetchKey: refreshKey }
  );

  type DigestJobNode = NonNullable<
    NonNullable<DigestJobsQuery["response"]["me"]["organization"]["digestJobs"]>[number]
  >;

  const digestJobs = useMemo<DigestJobNode[]>(() => {
    const jobs = data.me.organization.digestJobs ?? [];
    return jobs.filter((job): job is DigestJobNode => Boolean(job));
  }, [data.me.organization.digestJobs]);

  const sortedJobs = useMemo(
    () => [...digestJobs].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [digestJobs],
  );

  // Client-side filtering for team and status
  const filteredJobs = useMemo(() => {
    return sortedJobs.filter((job) => {
      if (teamFilter.size > 0 && !teamFilter.has(job.team?.id ?? "")) {
        return false;
      }
      if (statusFilter.size > 0 && !statusFilter.has(job.status ?? "")) {
        return false;
      }
      return true;
    });
  }, [sortedJobs, teamFilter, statusFilter]);

  // Get unique team and status values for filter options
  const teamOptions = useMemo(() => {
    const teamsMap = new Map<string, string>();
    digestJobs.forEach((job) => {
      if (job.team?.id && job.team?.name) {
        teamsMap.set(job.team.id, job.team.name);
      }
    });
    return Array.from(teamsMap.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [digestJobs]);

  const statusOptions = useMemo(() => {
    const statuses = new Set<string>();
    digestJobs.forEach((job) => {
      if (job.status) statuses.add(job.status);
    });
    return Array.from(statuses).map((status) => ({
      value: status,
      label: status === "ACTIVE" ? "Active" : status === "PAUSED" ? "Paused" : status,
    }));
  }, [digestJobs]);

  const handleRefresh = React.useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const formatSchedule = React.useCallback((schedule: unknown) => {
    if (!schedule || typeof schedule !== "object") {
      return "Not configured";
    }

    const scheduleObj = schedule as { cron_expression?: string };
    const cron = scheduleObj.cron_expression;

    if (!cron) {
      return "Not configured";
    }

    return formatCronExpression(cron) || cron;
  }, []);

  const formatRunTimestamp = React.useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const utcTime = format(utcDate, "MMM d, HH:mm");
    const localTime = format(date, "MMM d, HH:mm");
    return `${utcTime} UTC (${localTime} Local)`;
  }, []);

  const columns = useMemo<ColumnDef<DigestJobNode>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        meta: { headerClassName: "w-[25%]" },
        cell: ({ row }) => (
          <Link
            to={`/digests/job/${row.original.id}`}
            className="text-sm text-primary transition-colors hover:text-primary/80 hover:no-underline"
          >
            {row.original.name || "Unnamed"}
          </Link>
        ),
      },
      {
        id: "team",
        accessorFn: (row) => row.team?.name ?? "No team",
        header: ({ column }) => (
          <FilterableHeader
            column={column}
            title="Team"
            filterOptions={teamOptions}
            selectedValues={teamFilter}
            onFilterChange={setTeamFilter}
          />
        ),
        meta: { headerClassName: "w-[15%]" },
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">{row.original.team?.name || "No team"}</div>
        ),
      },
      {
        id: "status",
        accessorFn: (row) => row.status ?? "",
        header: ({ column }) => (
          <FilterableHeader
            column={column}
            title="Status"
            filterOptions={statusOptions}
            selectedValues={statusFilter}
            onFilterChange={setStatusFilter}
          />
        ),
        meta: { headerClassName: "w-[10%]" },
        enableSorting: false,
        cell: ({ row }) => (
          <JobStatusTag jobId={row.original.id} status={row.original.status} onStatusChanged={handleRefresh} readOnly={!canModify} />
        ),
      },
      {
        id: "schedule",
        accessorFn: (row) => row.schedule,
        header: "Schedule",
        meta: { headerClassName: "w-[30%]" },
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">{formatSchedule(row.original.schedule)}</div>
        ),
      },
      {
        id: "recentRuns",
        header: "Recent Runs",
        meta: { headerClassName: "w-[20%]" },
        enableSorting: false,
        cell: ({ row }) => (
          <RecentRunsCell
            runs={row.original.jobRuns?.edges ?? []}
            formatRunTimestamp={formatRunTimestamp}
          />
        ),
      },
    ],
    [formatSchedule, handleRefresh, canModify, teamOptions, teamFilter, statusOptions, statusFilter, formatRunTimestamp],
  );

  // Check for URL parameters to auto-open create digest dialog and detect onboarding mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const onboardingParam = urlParams.get("onboarding");

    if (onboardingParam) {
      const step = parseInt(onboardingParam, 10);
      setOnboardingStep(isNaN(step) ? null : step);
    }

    if (urlParams.get("create") === "true") {
      setIsCreateDialogOpen(true);
      const template = urlParams.get("template");
      if (template === "oncall_retrospective") {
        setTemplateKey("oncall_retrospective");
      }
      // Clean up create/template parameters but keep onboarding step
      const newUrl = onboardingParam
        ? `${window.location.pathname}?onboarding=${onboardingParam}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const handleCreateDigest = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setTemplateKey(null);
  };

  const userTeamName = me.teamContext?.team?.name || "your team";
  const template = templateKey ? getDigestTemplate(templateKey, userTeamName) : null;

  const isOnboarding = onboardingStep !== null;
  const hasNoDigestsForTeam = filteredJobs.length === 0;

  if (hasNoDigestsForTeam && !isOnboarding) {
    return (
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <DigestEmptyState onCreateClick={handleCreateDigest} />

        {isCreateDialogOpen && (
          <PageSuspense>
            <CreateDigestWizard
              organization={data.me.organization}
              isOpen={isCreateDialogOpen}
              onClose={() => {
                handleCloseCreateDialog();
                handleRefresh();
              }}
              teamId={userTeamId || ""}
              isOnboarding={false}
              onboardingStep={null}
              initialName={template?.name}
              initialSections={template?.sections}
              initialCronExpression={template?.cronExpression}
              onSuccess={(jobId) => {
                if (jobId) {
                  navigate(`/digests/job/${jobId}`);
                } else {
                  handleCloseCreateDialog();
                  handleRefresh();
                }
              }}
            />
          </PageSuspense>
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto custom-scrollbar">
      <div className="pl-6 pr-6 pt-4 pb-4">
        {/* Onboarding Banner */}
        {isOnboarding && (
          <UITourBanner
            text="Let's create your first digest agent!"
            onboardingStep={onboardingStep}
          />
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h1 className="text-xl font-medium text-foreground">Digest Agents</h1>
            <Tooltip
              content="AI agents that generate scheduled reports with trends, summaries, and actionable insights."
              position="right"
              maxWidth="16rem"
            >
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
          <ScopeGate scope="write:digests">
            <Button
              size="sm"
              onClick={handleCreateDigest}
            >
              Create Digest Agent
            </Button>
          </ScopeGate>
        </div>
      </div>
      <div className="px-6 pb-6">
        <div className="space-y-3">
          <SimpleDataTable
            data={filteredJobs}
            columns={columns}
            enableSorting
            enableCellFormatting
            getRowId={(row) => row.id}
            emptyState={<span className="text-muted-foreground">No digest agents found. Create one to get started.</span>}
          />
        </div>
      </div>

      {/* Create Digest Agent Wizard */}
      {isCreateDialogOpen && (
        <PageSuspense>
          <CreateDigestWizard
            organization={data.me.organization}
            isOpen={isCreateDialogOpen}
            onClose={() => {
              handleCloseCreateDialog();
              handleRefresh(); // Refresh data after creation
            }}
            teamId={userTeamId || ""}
            isOnboarding={isOnboarding}
            onboardingStep={onboardingStep}
            initialName={template?.name}
            initialSections={template?.sections}
            initialCronExpression={template?.cronExpression}
            onSuccess={(jobId) => {
              // Redirect to job details page after creation
              if (jobId) {
                const url = isOnboarding && onboardingStep
                  ? `/digests/job/${jobId}?onboarding=${onboardingStep}`
                  : `/digests/job/${jobId}`;
                navigate(url);
              } else {
                handleCloseCreateDialog();
                handleRefresh();
              }
            }}
          />
        </PageSuspense>
      )}
    </div>
  );
}

export default function DigestJobs() {
  return <DigestJobsContent />;
}
